import { Autocomplete, Box, TextField } from "@mui/material";
import { useState } from "react";
import { useGroups } from "@/hooks/useGroups";
import { useOrganizations } from "@/hooks/useOrganizations";
import type { GroupNode } from "@/interfaces/group";
import type { Organization } from "@/interfaces/organization";

/**
 * Server-searched Organization + dependent Group selectors for the Finance blotter. Organization is a
 * typeahead against the paginated orgs endpoint (never loads all orgs); Group populates from the chosen
 * org's groups (bounded). Emits ids to the parent, which passes them to the paginated invoices query —
 * so nothing sensitive is loaded client-side, and non-unique group names stay scoped to their org.
 */
export default function OrgGroupFilter({
  onOrgChange,
  onGroupChange,
}: {
  onOrgChange: (orgId: number | null) => void;
  onGroupChange: (groupId: number | null) => void;
}) {
  const [org, setOrg] = useState<Organization | null>(null);
  const [orgSearch, setOrgSearch] = useState("");
  const { data: orgsResp, isFetching: orgsLoading } = useOrganizations({
    search: orgSearch || undefined,
    limit: 20,
  });
  const orgs = orgsResp?.data ?? [];

  return (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
      <Autocomplete<Organization>
        options={orgs}
        loading={orgsLoading}
        value={org}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onInputChange={(_, v, reason) => reason === "input" && setOrgSearch(v)}
        onChange={(_, v) => {
          setOrg(v);
          onOrgChange(v?.id ?? null);
          onGroupChange(null); // group is scoped to the org; reset it
        }}
        size="small"
        sx={{ width: 260 }}
        renderInput={(p) => <TextField {...p} label="Organization" placeholder="Search…" />}
      />
      {org != null && <GroupSelect orgId={org.id} onGroupChange={onGroupChange} />}
    </Box>
  );
}

/** Rendered only once an org is chosen, so the org's groups (≤ handful) load — never a global list. */
function GroupSelect({
  orgId,
  onGroupChange,
}: {
  orgId: number;
  onGroupChange: (groupId: number | null) => void;
}) {
  const { data: groups = [], isFetching } = useGroups(orgId);
  const [group, setGroup] = useState<GroupNode | null>(null);

  return (
    <Autocomplete<GroupNode>
      options={groups}
      loading={isFetching}
      value={group}
      getOptionLabel={(g) => g.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, v) => {
        setGroup(v);
        onGroupChange(v?.id ?? null);
      }}
      size="small"
      sx={{ width: 260 }}
      renderInput={(p) => <TextField {...p} label="Group" placeholder="All groups" />}
    />
  );
}
